/* eslint-disable import/no-cycle */
/* eslint-disable no-nested-ternary */
/* istanbul ignore file */
import React, { useState } from 'react';
import {
  Flex,
  Box,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Button,
  Input,
  Checkbox,
  Heading,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Divider,
  Tooltip,
  useToast,
  ModalCloseButton,
  IconButton,
} from '@chakra-ui/core';
import { MdOutlineInfo } from 'react-icons/md';
import Joi from 'joi';
import { useTranslation } from 'react-i18next';
import isNaN from 'lodash/isNaN';
import isNil from 'lodash/isNil';
import axios, { AxiosError } from 'axios';
import { CloseIcon } from '@chakra-ui/icons';
import { clearErrorMessage, errorFormat, FormattedError, getErrorMessage, hasErrorMessage } from '../../../utils/FormErrorUtils';
import { FitbandTemplateType, FitbandType } from './FitbandConfigurationTypes';
import { useStoreActions, useStoreState } from '../../../models/hooks';
import { FitbandCalculationConfiguration } from './FitbandConfigurationView';
import colors from '../../../styles/colors';
import { AvailabilityEvaluation } from './AvailabilityEvaluation';

type FitbandTemplateProps = {
  isOpen: boolean;
  onConfirmOpen: () => void;
  onClose: () => void;
  templateData: FitbandType;
  setTemplateData: React.Dispatch<React.SetStateAction<FitbandType>>;
  fitbandTemplateEdit: FitbandTemplateType | null;
  setFitbandTemplateEdit: React.Dispatch<React.SetStateAction<FitbandTemplateType | null>>;
  fitbandTemplateView: FitbandTemplateType | null;
  setFitbandTemplateView: React.Dispatch<React.SetStateAction<FitbandTemplateType | null>>;
  formErrors: FormattedError[];
  setFormErrors: React.Dispatch<React.SetStateAction<FormattedError[]>>;
  getFitbandTemplates: () => void;
  isDisabled: boolean;
  setIsDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  penaltyData: FitbandCalculationConfiguration;
  setPenaltyData: React.Dispatch<React.SetStateAction<FitbandCalculationConfiguration>>;
};

export const FitbandTemplateCreateModal = ({
  isOpen,
  onConfirmOpen,
  onClose,
  templateData,
  setTemplateData,
  fitbandTemplateEdit,
  setFitbandTemplateEdit,
  fitbandTemplateView,
  setFitbandTemplateView,
  formErrors,
  setFormErrors,
  getFitbandTemplates,
  isDisabled,
  setIsDisabled,
  penaltyData,
  setPenaltyData,
}: FitbandTemplateProps): JSX.Element => {
  const { t } = useTranslation('administration');
  const toast = useToast();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const accountId = useStoreState((s) => s.app.accounts?.id);
  const isRequisitionEnabled = useStoreState((state) => state.app.accounts?.configuration?.requisitionEnabled);

  const { createFitbandTemplate } = useStoreActions((actions) => actions.fitbandTemplate);

  const [isTestingOpen, setIsTestingOpen] = useState<boolean>(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);
  const [isReqSubmitted, setIsReqSubmitted] = useState<boolean>(false);
  const [isExtraSubmitted, setIsExtraSubmitted] = useState<boolean>(false);
  const [isFitbandCreateLoading, setIsFitbandCreateLoading] = useState<boolean>(false);

  const [isUnsavedChangesModalOpen, setUnsavedChangesModalOpen] = useState<boolean>(false);

  const [fitbandErrors, setFitbandErrors] = useState({
    goodFit: false,
    mediumFit: false,
  });

  const getFitbandTemplateUpdate = (template: FitbandTemplateType) => {
    if (!isNil(template)) {
      setTemplateData((prevData) => ({
        ...prevData,
        name: template.name || prevData.name,
        goodFit: template.fitBand?.goodFit || prevData.goodFit,
        mediumFit: template.fitBand?.mediumFit || prevData.mediumFit,
      }));

      const fitbandConfig = template.fitBandCalculationConfigurations || {};
      const requisitionChecked = fitbandConfig.shouldPenalizeRequiredTime || false;
      const availabilityChecked = fitbandConfig.shouldPenalizeExtraTime || false;

      setPenaltyData((prevData) => ({
        ...prevData,
        requisitionChecked,
        availabilityChecked,
        reqDays: requisitionChecked ? fitbandConfig?.penalizeFactorRequiredDays ?? prevData.reqDays : 0,
        reqHours: requisitionChecked ? fitbandConfig?.penalizeFactorRequiredHours ?? prevData.reqHours : 0,
        extraDays: availabilityChecked ? fitbandConfig?.penalizeFactorExtraDays ?? prevData.extraDays : 0,
        extraHours: availabilityChecked ? fitbandConfig?.penalizeFactorExtraHours ?? prevData.extraHours : 0,
      }));
    }
  };

  React.useEffect(() => {
    if (fitbandTemplateEdit) {
      getFitbandTemplateUpdate(fitbandTemplateEdit);
    } else if (fitbandTemplateView) {
      getFitbandTemplateUpdate(fitbandTemplateView);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitbandTemplateEdit, fitbandTemplateView, setTemplateData]);

  const fitbandSchema = Joi.object()
    .options({ abortEarly: false })
    .keys({
      name: Joi.string()
        .trim(true)
        .required()
        .messages({
          'string.empty': t('fitbandConfiguration.fitbandModal.templateNameError'),
        }),
      goodFit: Joi.number().integer().min(1).max(100).required(),
      mediumFit: Joi.alternatives()
        .try(
          Joi.number()
            .integer()
            .min(1)
            .max(100)
            .custom((value, helpers) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              if (value >= helpers.state.ancestors[0].goodFit) {
                return helpers.error('mediumFit.invalid');
              }
              // eslint-disable-next-line @typescript-eslint/no-unsafe-return
              return value;
            }),
        )
        .allow(null, '')
        .messages({
          'mediumFit.invalid': t('fitbandConfiguration.fitbandModal.mediumFitError'),
          'number.min': '',
          'any.required': '',
        }),

      // Adding `requisitionChecked` to the schema
      requisitionChecked: Joi.boolean().required(),
      availabilityChecked: Joi.boolean().required(),

      reqDays: Joi.number()
        .min(0)
        .max(2)
        .precision(3)
        .when('requisitionChecked', {
          is: true,
          then: Joi.number().min(0).max(2).precision(3).required(),
          otherwise: Joi.optional().allow(''),
        }),

      reqHours: Joi.number()
        .min(0)
        .max(2)
        .precision(3)
        .when('requisitionChecked', {
          is: true,
          then: Joi.number().min(0).max(2).precision(3).required(),
          otherwise: Joi.optional().allow(''),
        }),

      extraDays: Joi.number()
        .min(0)
        .max(2)
        .precision(3)
        .when('availabilityChecked', {
          is: true,
          then: Joi.number().min(0).max(2).precision(3).required(),
          otherwise: Joi.optional().allow(''),
        }),

      extraHours: Joi.number()
        .min(0)
        .max(2)
        .precision(3)
        .when('availabilityChecked', {
          is: true,
          then: Joi.number().min(0).max(2).precision(3).required(),
          otherwise: Joi.optional().allow(''),
        }),
    });

  const formValidation = (formData: any, scheme: Joi.ObjectSchema) => {
    const { error } = scheme.validate(formData);
    if (error) {
      setFormErrors(errorFormat(error.details));
      return false;
    }
    setFormErrors([]);
    return true;
  };

  const hasError = (templateData.goodFit === 0 && isFormSubmitted) || (templateData.mediumFit === 0 && isFormSubmitted);

  React.useEffect(() => {
    if (hasError && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormSubmitted, hasError]);

  const handleInputChange = (value: string, fieldName: string) => {
    setTemplateData((prevData) => ({
      ...prevData,
      [fieldName]: value,
    }));
    setFormErrors(clearErrorMessage(formErrors, fieldName));
  };

  const handleBandInputChange = (value: string, fieldName: string) => {
    const MIN_VALUE = 0;
    const MAX_VALUE = 100;
    const numericValue = Math.min(MAX_VALUE, Math.max(MIN_VALUE, Number(value)));
    if (!isNaN(numericValue)) {
      setTemplateData((prevData) => {
        const updatedData = { ...prevData, [fieldName]: numericValue || 0 };

        if (fieldName === 'mediumFit') {
          setFitbandErrors({
            ...fitbandErrors,
            mediumFit: numericValue >= updatedData.goodFit,
          });
        } else if (fieldName === 'goodFit') {
          setFitbandErrors({
            ...fitbandErrors,
            mediumFit: updatedData.mediumFit >= numericValue,
          });
        }

        return updatedData;
      });
    }
    setFormErrors(clearErrorMessage(formErrors, fieldName));
  };

  const handlePenaltyValueInput = (value: string, fieldName: string) => {
    if (value === '') {
      setPenaltyData((prevData) => ({
        ...prevData,
        [fieldName]: 0,
      }));
      return;
    }

    const num = Number(value);

    if (isNaN(num)) {
      return;
    }

    if (num >= 0 && num <= 2) {
      const numericValue = Math.trunc(num * 1000) / 1000;
      setPenaltyData((prevData) => ({
        ...prevData,
        [fieldName]: numericValue,
      }));
    }
  };
  const resetFromData = () => {
    setTemplateData({
      name: '',
      goodFit: 0,
      mediumFit: 0,
    });
    setPenaltyData((prevData) => ({
      ...prevData,
      requisitionChecked: false,
      availabilityChecked: false,
      reqDays: 0,
      reqHours: 0,
      extraDays: 0,
      extraHours: 0,
    }));
    setFitbandTemplateEdit(null);
  };

  // function to handle fitband template creation and edit
  const onFitbandTemplateCreate = async () => {
    setIsFormSubmitted(true);

    if (penaltyData.requisitionChecked) {
      setIsReqSubmitted(true);
    }

    if (penaltyData.availabilityChecked) {
      setIsExtraSubmitted(true);
    }

    const templateDataToVerify = {
      ...templateData,
      ...penaltyData,
    };
    const validate = formValidation(templateDataToVerify, fitbandSchema);

    if (!validate) return;

    const payload = {
      name: templateData.name,
      fitBand: {
        goodFit: templateData.goodFit,
        mediumFit: templateData.mediumFit,
      },
      fitBandCalculationConfigurations: {
        shouldPenalizeExtraTime: penaltyData.availabilityChecked,
        penalizeFactorExtraDays: penaltyData.availabilityChecked ? penaltyData.extraDays || 0 : 0,
        penalizeFactorExtraHours: penaltyData.availabilityChecked ? penaltyData.extraHours || 0 : 0,
        shouldPenalizeRequiredTime: penaltyData.requisitionChecked,
        penalizeFactorRequiredDays: penaltyData.requisitionChecked ? penaltyData.reqDays || 0 : 0,
        penalizeFactorRequiredHours: penaltyData.requisitionChecked ? penaltyData.reqHours || 0 : 0,
      },
    };

    if (!fitbandTemplateEdit) {
      setIsFitbandCreateLoading(true);
      try {
        if (accountId) {
          await createFitbandTemplate({ accountId, templateData: payload });

          toast({
            title: t('fitbandConfiguration.fitbandToastMessage.templateCreate'),
            description: t('fitbandConfiguration.fitbandToastMessage.templateCreateSuccess', {
              templateName: templateData.name,
            }),
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
          setIsFitbandCreateLoading(false);
          resetFromData();
          onClose();
          getFitbandTemplates();
        }
      } catch (err) {
        setIsFitbandCreateLoading(false);
        console.error('Error creating template', err);
        let defaultMessage = t('fitbandConfiguration.fitbandToastMessage.nameExists');
        const newError = err as AxiosError<{ message: string }>;
        if (axios.isAxiosError(newError)) {
          defaultMessage = newError.response?.data?.message || defaultMessage;
        }
        toast({
          description: defaultMessage,
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
        throw err;
      }
    } else {
      onClose();
      onConfirmOpen();
    }
  };

  const handleCancel = () => {
    resetFromData();
    setFormErrors([]);
    setFitbandTemplateView(null);
    setIsDisabled(false);
    onClose();
  };

  const getDisplayValue = React.useCallback(
    (
      check: keyof Pick<FitbandCalculationConfiguration, 'requisitionChecked' | 'availabilityChecked'>,
      key: keyof Omit<FitbandCalculationConfiguration, 'requisitionChecked' | 'availabilityChecked'>,
    ) => {
      if (!penaltyData[check]) return '';
      const value = penaltyData[key];
      if (value === 0) return '0';

      return value.toFixed(3).replace(/\.?0+$/, '');
    },
    [penaltyData],
  );

  const checkInputDisable = React.useCallback(
    (check: keyof Pick<FitbandCalculationConfiguration, 'requisitionChecked' | 'availabilityChecked'>) => {
      return !penaltyData[check] || isDisabled;
    },
    [penaltyData, isDisabled],
  );

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="4xl" scrollBehavior="inside" closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent data-testid="fitbandTemplateModal" overflow="auto" p={4}>
          <ModalHeader borderBottom={`1px solid ${colors.lavenderMist}`}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Heading as="h4" fontSize="lg" lineHeight="1.3">
                {fitbandTemplateView
                  ? t('fitbandConfiguration.fitbandModal.viewFitband')
                  : fitbandTemplateEdit
                  ? t('fitbandConfiguration.fitbandModal.editFitband')
                  : t('fitbandConfiguration.fitbandModal.createFitband')}
              </Heading>
              {!isTestingOpen && !isDisabled ? (
                <Box
                  onClick={() => setIsTestingOpen(true)}
                  data-testid="testSettings"
                  cursor="pointer"
                  fontSize="sm"
                  fontWeight={400}
                  color={colors.blue[500]}
                >
                  {t('fitbandConfiguration.fitbandModal.testSettings')}
                </Box>
              ) : (
                // <ModalCloseButton position="relative" inset={0} onClick={() => setUnsavedChangesModalOpen(true)} />
                <IconButton
                  aria-label="Close modal"
                  icon={<CloseIcon />}
                  size="sm"
                  variant="ghost"
                  // onClick={() => setUnsavedChangesModalOpen(true)}
                  onClick={handleCancel}
                  position="absolute"
                  top="1rem"
                  right="1rem"
                />
              )}
            </Box>
          </ModalHeader>
          <ModalBody>
            <Box pt={4}>
              <FormControl isInvalid={hasErrorMessage(formErrors, 'name')} isRequired={!fitbandTemplateView}>
                <FormLabel fontSize="14px" fontWeight={700}>
                  {t('fitbandConfiguration.fitbandModal.templateName')}
                </FormLabel>
                <Input
                  data-testid="templateIdField"
                  value={templateData.name}
                  onChange={(e) => {
                    const trimmedName = e.target.value
                      .replace(/[^a-zA-Z0-9\s.\-_]/g, '')
                      .replace(/\s+/g, ' ')
                      .trimStart();
                    handleInputChange(trimmedName, 'name');
                  }}
                  placeholder={t('fitbandConfiguration.fitbandModal.enterTemplateName')}
                  fontSize="14px"
                  maxLength={100}
                  minLength={3}
                  backgroundColor={colors.input.background}
                  _placeholder={{ color: colors.gray[400] }}
                  style={{ height: '40px' }}
                  disabled={isDisabled}
                  _disabled={{ color: colors.black }}
                />
                <FormErrorMessage>{getErrorMessage(formErrors, 'name')}</FormErrorMessage>
              </FormControl>

              <Box mt={5} display="flex" justifyContent="flex-start" alignItems="baseline" width="100%">
                <FormControl isInvalid={templateData.goodFit === 0 && isFormSubmitted} isRequired={!fitbandTemplateView}>
                  <Flex justifyContent="flex-start" alignItems="baseline" width="50%">
                    <FormLabel fontSize="14px" fontWeight={700}>
                      {t('fitbandConfiguration.fitbandModal.goodFit')}
                    </FormLabel>
                    <Input
                      type="tel"
                      width="50px"
                      size="sm"
                      placeholder="100"
                      maxLength={3}
                      value={templateData.goodFit === 0 ? '' : templateData.goodFit}
                      onChange={(e) => handleBandInputChange(e.target.value, 'goodFit')}
                      disabled={isDisabled}
                      _placeholder={{ color: colors.gray[400] }}
                      _disabled={{ borderWidth: '0' }}
                      ref={inputRef}
                    />
                  </Flex>
                </FormControl>

                <FormControl
                  isInvalid={
                    Boolean(getErrorMessage(formErrors, 'mediumFit')) ||
                    (templateData.mediumFit === 0 && isFormSubmitted) ||
                    fitbandErrors.mediumFit
                  }
                  isRequired={!fitbandTemplateView}
                >
                  <Flex justifyContent="flex-start" alignItems="baseline" width="50%">
                    <FormLabel fontSize="14px" fontWeight={700} mt={1}>
                      {t('fitbandConfiguration.fitbandModal.mediumFit')}
                    </FormLabel>
                    <Input
                      type="tel"
                      width="50px"
                      size="sm"
                      placeholder="50"
                      maxLength={3}
                      value={templateData.mediumFit === 0 ? '' : templateData.mediumFit}
                      onChange={(e) => handleBandInputChange(e.target.value, 'mediumFit')}
                      disabled={isDisabled}
                      _placeholder={{ color: colors.gray[400] }}
                      _disabled={{ borderWidth: '0' }}
                      ref={inputRef}
                    />
                  </Flex>
                  <FormErrorMessage>{getErrorMessage(formErrors, 'mediumFit')}</FormErrorMessage>
                </FormControl>
              </Box>
              <Divider color={colors.lavenderMist} fontSize="0.25px" mt={5} mb={4} />

              <Flex justifyContent="flex-start" alignItems="center" mb={1}>
                <Text fontSize="18px" fontWeight={700} mr={1}>
                  {t('fitbandConfiguration.fitbandModal.penalty')}
                </Text>
                <Tooltip hasArrow label={t('fitbandConfiguration.penaltyTooltipText')} placement="right">
                  <Box>
                    <MdOutlineInfo size={17} color="#718096" />
                  </Box>
                </Tooltip>
              </Flex>

              <Flex width="100%" justifyContent="center" alignItems="center">
                <Box width="50%">
                  <Flex justifyContent="flex-start" alignItems="center">
                    <Checkbox
                      mt={4}
                      mb={4}
                      data-testid="requisitionCheckbox"
                      isChecked={penaltyData.requisitionChecked}
                      onChange={(e) => {
                        setPenaltyData((prevData) => ({
                          ...prevData,
                          requisitionChecked: e.target.checked,
                        }));
                      }}
                      isDisabled={isDisabled}
                    >
                      <Text fontSize="14px" fontWeight={700}>
                        {t('fitbandConfiguration.fitbandModal.requisition')}
                      </Text>
                    </Checkbox>
                    <Tooltip hasArrow label={t('fitbandConfiguration.requisitionText')} placement="right">
                      <Box ml={1}>
                        <MdOutlineInfo size={17} color="#718096" />
                      </Box>
                    </Tooltip>
                  </Flex>
                  <Flex justifyContent="center" alignItems="center" width="100%">
                    <Text
                      fontSize="14px"
                      fontWeight={400}
                      width="35%"
                      color={penaltyData.requisitionChecked || isDisabled ? 'default' : 'grey'}
                    >
                      {t('fitbandConfiguration.fitbandModal.days')}
                    </Text>
                    <FormControl
                      isInvalid={String(penaltyData.reqDays) === '' && penaltyData.requisitionChecked && isReqSubmitted}
                      isRequired={penaltyData.requisitionChecked}
                      isDisabled={checkInputDisable('requisitionChecked')}
                    >
                      <Input
                        type="number"
                        width="100px"
                        size="sm"
                        placeholder="0"
                        step="0.001"
                        min="0"
                        max="2"
                        value={getDisplayValue('requisitionChecked', 'reqDays')}
                        onChange={(e) => handlePenaltyValueInput(e.target.value, 'reqDays')}
                      />
                    </FormControl>
                  </Flex>
                  <Flex justifyContent="center" alignItems="center" width="100%" mt={3}>
                    <Text
                      fontSize="14px"
                      fontWeight={400}
                      width="35%"
                      color={penaltyData.requisitionChecked || isDisabled ? 'default' : 'grey'}
                    >
                      {t('fitbandConfiguration.fitbandModal.hours')}
                    </Text>
                    <FormControl
                      isInvalid={String(penaltyData.reqHours) === '' && penaltyData.requisitionChecked && isReqSubmitted}
                      isRequired={penaltyData.requisitionChecked}
                      isDisabled={checkInputDisable('requisitionChecked')}
                    >
                      <Input
                        type="number"
                        width="100px"
                        size="sm"
                        placeholder="0"
                        step="0.001"
                        min="0"
                        max="2"
                        value={getDisplayValue('requisitionChecked', 'reqHours')}
                        onChange={(e) => handlePenaltyValueInput(e.target.value, 'reqHours')}
                      />
                    </FormControl>
                  </Flex>
                </Box>
                <Box width="50%">
                  <Flex justifyContent="flex-start" alignItems="center">
                    <Checkbox
                      mt={4}
                      mb={4}
                      data-testid="availabilityCheckbox"
                      isChecked={penaltyData.availabilityChecked}
                      onChange={(e) => {
                        setPenaltyData((prevData) => ({
                          ...prevData,
                          availabilityChecked: e.target.checked,
                        }));
                      }}
                      isDisabled={isDisabled}
                    >
                      <Text fontSize="14px" fontWeight={700}>
                        {t('fitbandConfiguration.fitbandModal.extraAvailability')}
                      </Text>
                    </Checkbox>
                    <Tooltip hasArrow label={t('fitbandConfiguration.extraAvailabilityText')} placement="right">
                      <Box ml={1}>
                        <MdOutlineInfo size={17} color="#718096" />
                      </Box>
                    </Tooltip>
                  </Flex>
                  <Flex justifyContent="center" alignItems="center" width="100%">
                    <Text
                      fontSize="14px"
                      fontWeight={400}
                      width="35%"
                      color={penaltyData.availabilityChecked || isDisabled ? 'default' : 'grey'}
                    >
                      {t('fitbandConfiguration.fitbandModal.days')}
                    </Text>
                    <FormControl
                      isInvalid={penaltyData.extraDays.toString() === '' && penaltyData.availabilityChecked && isExtraSubmitted}
                      isRequired={penaltyData.availabilityChecked}
                      isDisabled={checkInputDisable('availabilityChecked')}
                    >
                      <Input
                        type="number"
                        width="100px"
                        size="sm"
                        placeholder="0"
                        step="0.001"
                        min="0"
                        max="2"
                        value={getDisplayValue('availabilityChecked', 'extraDays')}
                        onChange={(e) => handlePenaltyValueInput(e.target.value, 'extraDays')}
                      />
                    </FormControl>
                  </Flex>
                  <Flex justifyContent="center" alignItems="center" width="100%" mt={3}>
                    <Text
                      fontSize="14px"
                      fontWeight={400}
                      width="35%"
                      color={penaltyData.availabilityChecked || isDisabled ? 'default' : 'grey'}
                    >
                      {t('fitbandConfiguration.fitbandModal.hours')}
                    </Text>
                    <FormControl
                      isInvalid={String(penaltyData.extraHours) === '' && penaltyData.availabilityChecked && isExtraSubmitted}
                      isRequired={penaltyData.availabilityChecked}
                      isDisabled={checkInputDisable('availabilityChecked')}
                    >
                      <Input
                        type="number"
                        width="100px"
                        size="sm"
                        placeholder="0"
                        step="0.001"
                        min="0"
                        max="2"
                        value={getDisplayValue('availabilityChecked', 'extraHours')}
                        onChange={(e) => handlePenaltyValueInput(e.target.value, 'extraHours')}
                      />
                    </FormControl>
                  </Flex>
                </Box>
              </Flex>
            </Box>
            <ModalFooter pr={0} mt={2}>
              {!fitbandTemplateView && (
                //  (
                //   <Button data-testid="closeBtn" colorScheme="blue" onClick={handleCancel}>
                //     {t('fitbandConfiguration.okBtn')}
                //   </Button>
                // ):
                <>
                  <Button data-testid="cancelBtn" colorScheme="red" onClick={handleCancel}>
                    {t('fitbandConfiguration.cancelBtn')}
                  </Button>
                  <Button
                    isLoading={isFitbandCreateLoading}
                    data-testid="createTemplateBtn"
                    colorScheme="blue"
                    ml={3}
                    disabled={isFitbandCreateLoading}
                    onClick={onFitbandTemplateCreate}
                  >
                    {fitbandTemplateEdit
                      ? t('fitbandConfiguration.fitbandModal.updateBtn')
                      : t('fitbandConfiguration.fitbandModal.createBtn')}
                  </Button>
                </>
              )}
            </ModalFooter>

            {isTestingOpen && (
              <Box mt={4} borderTop={`1px solid ${colors.lavenderMist}`}>
                <AvailabilityEvaluation
                  accountId={accountId}
                  setTestingOpen={setIsTestingOpen}
                  templateData={templateData}
                  penaltyData={penaltyData}
                  formValidation={formValidation}
                  setRequisitionSubmitted={setIsReqSubmitted}
                  setExtraSubmitted={setIsExtraSubmitted}
                  setFormSubmitted={setIsFormSubmitted}
                  requisitionEnabled={isRequisitionEnabled}
                />
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={isUnsavedChangesModalOpen} onClose={() => setUnsavedChangesModalOpen(false)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Box>{t('fitbandConfiguration.fitbandModal.unsavedChanges')}</Box>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box>{t('fitbandConfiguration.fitbandModal.unsavedChangesText')}</Box>
          </ModalBody>
          <ModalFooter>
            <Button
              mr={2}
              colorScheme="red"
              onClick={() => {
                setUnsavedChangesModalOpen(false);
                handleCancel();
              }}
            >
              {t('fitbandConfiguration.cancelBtn')}
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => {
                setUnsavedChangesModalOpen(false);
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                onFitbandTemplateCreate();
              }}
            >
              {t('fitbandConfiguration.fitbandModal.saveBtn')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
